import { useCallback, useEffect, useState } from "react";
import { parsePaginatedResponse } from "../utils/paginatedList";

/**
 * Server-side paginated list fetch (page + limit + optional search).
 * @param {(params: { search: string, page: number, limit: number }) => Promise<{ data: unknown }>} fetchPage
 * @param {{ entriesPerPage?: number, debounceMs?: number, enabled?: boolean }} options
 */
export default function useServerPaginatedList(
  fetchPage,
  { entriesPerPage = 10, debounceMs = 400, enabled = true } = {}
) {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async () => {
    if (!enabled) return;
    setIsLoading(true);
    try {
      const response = await fetchPage({
        search: searchTerm,
        page: currentPage,
        limit: entriesPerPage,
      });
      const { rows: nextRows, total } = parsePaginatedResponse(response.data);
      setRows(nextRows);
      setTotalCount(total);
    } finally {
      setIsLoading(false);
    }
  }, [enabled, fetchPage, searchTerm, currentPage, entriesPerPage]);

  useEffect(() => {
    const delay = searchTerm ? debounceMs : 0;
    const id = setTimeout(() => {
      load();
    }, delay);
    return () => clearTimeout(id);
  }, [load, debounceMs, searchTerm]);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  return {
    rows,
    totalCount,
    searchTerm,
    setSearchTerm: handleSearchChange,
    currentPage,
    setCurrentPage,
    isLoading,
    entriesPerPage,
    refetch: load,
  };
}
